import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApprovalService, useSignatureService, useDocumentService, useMetadataService, useArbolDocumentalService } from '../services/api';
import { useUser } from '../hooks/useUser';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { extractValue } from '../utils/metadataUtils';


import {
  AiOutlineClose,
  AiOutlineFileText,
  AiOutlineReload,
  AiOutlineCheck,
  AiOutlineCloudServer,
  AiOutlineEdit,
  AiOutlineRollback,
  AiOutlineWarning
} from 'react-icons/ai';
import { FaEye, FaDownload, FaPenNib } from "react-icons/fa";
import Buscador from '../components/Buscador';
import { systemFieldsForTab } from '../config/searchFields';
import { columnsForTab } from '../config/optionalColumns';
import { colors } from '../theme/tokens';
import PDFViewer from '../components/PDFViewer';
import SignDocumentModal from '../components/Signature/SignDocumentModal';
import BatchSignatureModal from '../components/Signature/BatchSignatureModal';
import BatchResumeBanner from '../components/Signature/BatchResumeBanner';
import BatchProgressBar from '../components/BatchProgressBar';
import DocTreeSidebar, { findPath as findTreePath } from '../components/DocTreeSidebar/DocTreeSidebar';
import { useNotification } from '../hooks/useNotification';
import { usePolling } from '../hooks/usePolling';

// Dado un doc y la spec de su serie, devuelve los labels de los campos required vacíos.
// Array vacío = todos los obligatorios OK.
const getMissingRequiredFieldLabels = (doc, spec) => {
  if (!spec || spec.length === 0) return [];
  const md = doc?.metadata || {};
  return spec
    .filter((f) => f && f.required)
    .filter((f) => {
      const v = extractValue(md[f.name]);
      return v === null || v === undefined || String(v).trim() === '';
    })
    .map((f) => f.label || f.name);
};

const ApprovalPage = ({ onBack }) => {
  const navigate = useNavigate();
  const approvalService = useApprovalService();
  const signatureService = useSignatureService();
  const documentService = useDocumentService();
  const metadataService = useMetadataService(); // Servicio para tipos
  const arbolService = useArbolDocumentalService(); // Árbol completo para la sidebar
  const { hasRole, hasAnyRole } = useUser();
  const notify = useNotification();

  // Verificar roles
  const isReviewer = hasRole('REVIEWER');
  const isApprover = hasRole('APPROVER');
  const isSigner = hasRole('SIGNER');
  const hasAccess = hasAnyRole(['REVIEWER', 'APPROVER', 'SIGNER']);

  // Estado para tab activo. Por defecto OCR para reviewer/approver; si el usuario es solo
  // firmante, abrir directo en el tab de Firma.
  const [activeTab, setActiveTab] = useState(() => {
    if (isReviewer || isApprover) return 'ocr';
    if (isSigner) return 'signature';
    return 'ocr';
  });

  // Cambio de tab: preserva filtros del Buscador pero limpia selección y refresca (P2 #66)
  const changeTab = (tab) => {
    if (tab === activeTab) return;
    setSelectedDocIds([]);
    setActiveTab(tab);
    setRefreshTrigger(prev => prev + 1);
  };

  // Estado para el tipo de documento seleccionado
  const [selectedTipoDoc, setSelectedTipoDoc] = useState("");
  const [availableTypes, setAvailableTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [serieCounts, setSerieCounts] = useState({}); // { [idTipoDoc]: counts }
  const [metadataSpec, setMetadataSpec] = useState([]); // spec de la serie seleccionada
  // Árbol jerárquico (folders + series) para la sidebar — viene anidado del API
  // /doc-tree (con ACL aplicado), así que el usuario solo ve sus series visibles.
  const [arbolTree, setArbolTree] = useState([]);

  // Paginación (compartida para Buscador)
  const [pagination, setPagination] = useState({ total: 0, page: 0, size: 10 });

  // Estado para refrescar el buscador
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Estados para acciones
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Estado para PDF Viewer
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [viewingDocId, setViewingDocId] = useState(null);

  // Estado para el modal de firma (tab de Firma, #59 F1C)
  const [signDoc, setSignDoc] = useState(null);
  const [batchSign, setBatchSign] = useState(null); // { docIds?, filter?, count } | { resumeBatchId, count } | null

  // ========== BULK SELECTION STATE ==========
  const [selectedDocIds, setSelectedDocIds] = useState([]);

  /**
   * Saca un documento de la selección (#128).
   *
   * Toda acción de fila que cambia el estado de un documento lo saca de la bandeja, pero refrescar
   * la grilla no toca `selectedDocIds`: el documento quedaba seleccionado SIN fila donde destildarlo
   * —invisible— y viajaba igual en la acción masiva siguiente. Así un documento recién firmado se
   * coló en un lote y el preparador lo descartó con `NO_PENDING`.
   */
  const deselectDoc = (idDoc) => {
    if (idDoc == null) return;
    setSelectedDocIds((prev) => prev.filter((id) => id !== idDoc));
  };
  const [currentSearchFilter, setCurrentSearchFilter] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Seguimiento del batch asincrónico en curso (ADR-017 / FSD-010).
  // null ⇒ no hay batch activo. Cuando se dispara un batch, el 202 del API devuelve
  // un BatchTaskStatus con taskId; seteamos acá y arrancamos polling.
  const [activeBatchTask, setActiveBatchTask] = useState(null);
  const activeTaskIdRef = useRef(null);

  // Poller de status: se re-crea cuando cambia el taskId.
  const batchPolling = usePolling(
    async () => {
      const taskId = activeTaskIdRef.current;
      if (!taskId) return null;
      const resp = await approvalService.getBatchTaskStatus(taskId);
      return resp?.data;
    },
    {
      intervalMs: 2000,
      stopCondition: (status) => status && !status.running,
      onStop: (finalStatus) => {
        if (!finalStatus) return;
        setActiveBatchTask(finalStatus);
        const { fase, exitosos = 0, fallidos = 0, totalDocs = 0 } = finalStatus;
        if (fase === 'COMPLETADO' && fallidos === 0) {
          notify.success(`${exitosos}/${totalDocs} operados correctamente`);
        } else if (fase === 'COMPLETADO' && fallidos > 0) {
          notify.warning(`${exitosos} exitosos, ${fallidos} fallidos — ver detalle`);
        } else if (fase === 'CANCELADO') {
          notify.info(`Cancelado: ${exitosos} operados antes de cancelar`);
        } else if (fase === 'ERROR') {
          notify.error(`Error en la operación: ${finalStatus.mensaje || 'desconocido'}`);
        }
        setRefreshTrigger(prev => prev + 1);
      },
      onError: (err) => {
        if (err?.response?.status === 404) {
          notify.info('Información de progreso expirada');
          setActiveBatchTask(null);
          activeTaskIdRef.current = null;
        }
      },
    }
  );

  // Cuando el poller recibe data actualizada, la reflejamos en la barra.
  useEffect(() => {
    if (batchPolling.data) setActiveBatchTask(batchPolling.data);
  }, [batchPolling.data]);

  /**
   * Helper: acepta la respuesta del API (202 + BatchTaskStatus) y arranca el
   * seguimiento. Si el totalDocs es 0 no hace polling (nada que hacer).
   */
  const startBatchTracking = (response) => {
    const status = response?.data;
    if (!status || !status.taskId) return;
    activeTaskIdRef.current = status.taskId;
    setActiveBatchTask(status);
    if (status.totalDocs > 0) batchPolling.start();
  };

  const handleCancelBatch = async () => {
    const taskId = activeTaskIdRef.current;
    if (!taskId) return;
    try {
      const resp = await approvalService.cancelBatchTask(taskId);
      if (resp?.data) setActiveBatchTask(resp.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        notify.error('Sólo el que inició el batch o un admin pueden cancelarlo');
      } else if (err?.response?.status === 404) {
        notify.info('El batch ya terminó');
      } else {
        notify.error('Error al cancelar el batch');
      }
    }
  };

  const handleCloseBatch = () => {
    setActiveBatchTask(null);
    activeTaskIdRef.current = null;
    batchPolling.stop();
  };

  // Estados para modales de bulk actions
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [bulkActionType, setBulkActionType] = useState(null); // 'selected' or 'all'

  // Estado para confirmación de "Apply to All"
  const [showApplyAllModal, setShowApplyAllModal] = useState(false);
  const [applyAllAction, setApplyAllAction] = useState(null); // 'review', 'approve', 'reject'
  const [showRejectedReasonModal, setShowRejectedReasonModal] = useState(false);
  const [selectedRejectedDoc, setSelectedRejectedDoc] = useState(null);

  // Verificar acceso al montar
  useEffect(() => {
    if (!hasAccess) {
      notify.warning('No tienes permisos para acceder a esta página.');
      if (onBack) onBack();
      else navigate(-1);
    }
  }, [hasAccess, onBack, navigate, notify]);

  // Cargar tipos de documentos disponibles y sus conteos
  useEffect(() => {
    let cancelled = false;
    const loadTypes = async () => {
      try {
        setLoadingTypes(true);
        const response = await metadataService.getTiposDocumentos();
        if (cancelled) return;
        const types = Array.isArray(response.data) ? response.data : (response.data?.items || []);
        setAvailableTypes(types);

        // Cargar conteos por serie en paralelo
        const counts = {};
        const results = await Promise.all(types.map(async (tipo) => {
          try {
            const res = await metadataService.getSerieCounts(tipo.id);
            return { id: tipo.id, data: res.data || {} };
          } catch {
            return { id: tipo.id, data: {} };
          }
        }));
        if (cancelled) return;
        results.forEach(({ id, data }) => { counts[id] = data; });
        setSerieCounts(counts);
      } catch (e) {
        console.error("Error cargando tipos de documentos:", e);
        if (!cancelled) notify.error("Error cargando tipos de documentos");
      } finally {
        if (!cancelled) setLoadingTypes(false);
      }
    };
    loadTypes();
    return () => { cancelled = true; };
  }, []);

  // Cargar el árbol documental anidado (folders + series con ACL del usuario).
  // Se necesita para mostrar la jerarquía en la sidebar; las series planas de
  // `availableTypes` quedan para los flujos existentes (search, conteos).
  useEffect(() => {
    let cancelled = false;
    arbolService.getArbolDocumental()
      .then((response) => {
        if (cancelled) return;
        setArbolTree(response?.data?.folders || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error cargando árbol documental:', err);
      });
    return () => { cancelled = true; };
  }, []);

  // Path raíz→serie del nodo actualmente seleccionado, para que la sidebar
  // auto-expanda los ancestros y resalte la serie.
  const selectedTreePath = useMemo(() => {
    if (!selectedTipoDoc) return [];
    return findTreePath(arbolTree, selectedTipoDoc) || [];
  }, [selectedTipoDoc, arbolTree]);

  /**
   * Campos del sistema que ofrece el buscador en la pestaña actual (issue UBA #43).
   *
   * No es una lista fija: un campo se ofrece cuando *puede tener valor* en ese estado. Cada
   * pestaña fuerza su estado del lado del servidor, así que lo que todavía no ocurrió está
   * vacío en todas las filas — un filtro que sólo puede mentir.
   *
   * `approvalRequired` importa únicamente en la pestaña de firma: si la serie no exige
   * aprobación, el documento pasa de revisión directo a firma y los campos de aprobación
   * quedan vacíos.
   */
  /**
   * Configuración del circuito de la serie elegida. Decide qué pasos ya ocurrieron y por lo
   * tanto qué campos pueden tener valor: sin revisión, `reviewed_by`/`reviewed_at` quedan NULL
   * para siempre porque el documento va de importación directo a aprobación o a firma
   * (`WorkflowService.handleImportingStatus`).
   */
  const circuitoSerie = useMemo(() => {
    const serie = availableTypes.find((t) => t.id === selectedTipoDoc);
    return {
      approvalRequired: !!serie?.approvalRequired,
      reviewRequired: !!serie?.reviewRequired,
    };
  }, [availableTypes, selectedTipoDoc]);

  const camposDeSistema = useMemo(
    () => systemFieldsForTab(activeTab, circuitoSerie),
    [activeTab, circuitoSerie],
  );

  /**
   * Columnas de la grilla, con la misma regla que los filtros y derivadas del mismo origen:
   * las fijas muestran el dato del paso que acaba de ocurrir y las opcionales son el resto de
   * lo que esa pestaña puede mostrar.
   */
  const columnasDeLaPestaña = useMemo(
    () => columnsForTab(activeTab, circuitoSerie),
    [activeTab, circuitoSerie],
  );

  // Click en el árbol: si es serie → seleccionar (limpia docs seleccionados);
  // si es folder → solo expand/collapse (el árbol lo hace antes de invocar este
  // callback, así que acá basta con ignorar el evento de folder).
  const handleTreeNodeClick = (path) => {
    if (!path || path.length === 0) return;
    const last = path[path.length - 1];
    if (last.isSeries) {
      setSelectedTipoDoc(last.id);
      setSelectedDocIds([]);
    }
  };

  // Cargar spec de metadata de la serie seleccionada — usado para deshabilitar "Aprobar
  // Revisión" cuando faltan obligatorios.
  useEffect(() => {
    if (!selectedTipoDoc) { setMetadataSpec([]); return; }
    documentService.getMetadataSpec(selectedTipoDoc)
      .then((res) => setMetadataSpec(res?.data?.fieldSpec || []))
      .catch(() => setMetadataSpec([]));
  }, [selectedTipoDoc]);

  // Refrescar conteos de la serie seleccionada luego de acciones
  const prevRefreshRef = useRef(refreshTrigger);
  useEffect(() => {
    if (!selectedTipoDoc || refreshTrigger === prevRefreshRef.current) return;
    prevRefreshRef.current = refreshTrigger;
    metadataService.getSerieCounts(selectedTipoDoc)
      .then((res) => {
        setSerieCounts((prev) => ({ ...prev, [selectedTipoDoc]: res.data || {} }));
      })
      .catch(() => {});
  }, [refreshTrigger, selectedTipoDoc]);

  // Función de búsqueda override para Buscador
  const handleSearchOverride = async (filters, paginationData, sortData) => {
    // Construir el payload según el formato requerido por el usuario/API
    const payload = {
      filters: [],
      filterGroups: [],
      sort: sortData || []
    };

    // Agregar el tipo de documento como filtro 'term' en el array 'filters'
    if (selectedTipoDoc) {
      payload.filters.push({
        field: "id_tipo_doc",
        operator: "term",
        value: selectedTipoDoc
      });
    }

    // Agregar los grupos de filtros dinámicos que vienen del Buscador
    if (filters && filters.filterGroups) {
      payload.filterGroups = filters.filterGroups;
    }

    // Guardar el filtro actual para operaciones "Apply to All"
    setCurrentSearchFilter(payload);

    // Llamar al servicio correspondiente según tab
    if (activeTab === 'ocr') return approvalService.getPendingOcr(paginationData, payload, sortData);
    if (activeTab === 'review') return approvalService.getPendingReview(paginationData, payload, sortData);
    if (activeTab === 'approval') return approvalService.getPendingApproval(paginationData, payload, sortData);
    if (activeTab === 'signature') return signatureService.getPendingSignature(paginationData, payload, sortData);
    return approvalService.getRejected(paginationData, payload, sortData);
  };

  // Acciones personalizadas
  const handleApprove = async (doc) => {
    try {
      await approvalService.approveDocument(doc.idDoc);
      deselectDoc(doc.idDoc); // #128
      notify.success('Documento aprobado');
      setRefreshTrigger(prev => prev + 1);
    } catch (e) {
      notify.error('Error al aprobar');
    }
  };

  const handleReject = (doc) => {
    setSelectedDoc(doc);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!selectedDoc) return;
    // Rechazo de firma (#59 F1C). El server toma y libera el lock por su cuenta (#108): antes había
    // que pedírselo llamando primero a `start` —el endpoint de la FIRMA— sólo para conseguir el
    // token, lo que ataba rechazar a firmar y dejaba dos llamadas donde alcanza una.
    if (activeTab === 'signature') {
      if (!rejectReason.trim()) {
        notify.warning('Debe ingresar un motivo de rechazo');
        return;
      }
      try {
        await signatureService.rejectSignature(selectedDoc.idDoc, rejectReason);
        deselectDoc(selectedDoc.idDoc); // #128
        notify.success('Documento rechazado');
        setShowRejectModal(false);
        setRefreshTrigger(prev => prev + 1);
      } catch (e) {
        notify.error(e?.response?.status === 409
          ? 'El documento está siendo firmado por otro usuario.'
          : 'Error al rechazar');
      }
      return;
    }
    try {
      const mode = activeTab === 'approval' || (!isReviewer && isApprover) ? 'approval' : 'review';
      await approvalService.rejectDocument(selectedDoc.idDoc, rejectReason, mode);
      deselectDoc(selectedDoc.idDoc); // #128
      notify.success('Documento rechazado');
      setShowRejectModal(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (e) {
      notify.error('Error al rechazar');
    }
  };

  // Firma confirmada OK (modal de firma) → refrescar la lista del tab de Firma.
  const handleSigned = () => {
    deselectDoc(signDoc?.idDoc); // #128: ya está publicado, no puede quedar seleccionado para firmar
    setSignDoc(null);
    notify.success('Documento firmado y publicado');
    setRefreshTrigger(prev => prev + 1);
  };

  const handleRerunOCR = async (doc) => {
    try {
      await approvalService.rerunOCR(doc.idDoc);
      deselectDoc(doc.idDoc); // #128
      notify.success('OCR re-programado');
      // Disparar refresh: el doc transiciona a OCR_PENDING y debe salir de esta lista.
      setRefreshTrigger(prev => prev + 1);
    } catch (e) {
      notify.error('Error al reejecutar OCR');
    }
  };

  const handleCompleteReview = async (doc) => {
    try {
      // Completar revisión (aprobar revisión)
      await approvalService.completeReview(doc.idDoc);
      deselectDoc(doc.idDoc); // #128
      notify.success('Revisión completada');
      setRefreshTrigger(prev => prev + 1);
    } catch (e) {
      // Si el backend devolvió ProblemDetails con errores por campo, mostrarlos.
      const errors = e?.response?.data?.errors;
      if (Array.isArray(errors) && errors.length > 0) {
        const detalles = errors.map(err => err.message || err.field).join(' — ');
        notify.error(`No se puede aprobar: ${detalles}`);
      } else {
        notify.error('Error al completar revisión');
      }
    }
  };

  const handleMetadataEdit = async (docId, newMetadata) => {
    try {
      await approvalService.updateReviewMetadata(docId, newMetadata);
      notify.success('Metadatos actualizados');
      setRefreshTrigger(prev => prev + 1);
    } catch (e) {
      console.error(e);
      notify.error('Error al actualizar metadatos');
      throw e; // Para que DocumentGrid sepa que falló si manejara promesas
    }
  };

  // ========== BULK ACTIONS ==========

  // Bulk Review (for selected documents) — async con barra de progreso.
  const handleBulkReview = async () => {
    if (selectedDocIds.length === 0 || bulkLoading) return;
    setBulkLoading(true);
    try {
      const resp = await approvalService.reviewDocumentsBatch(selectedDocIds);
      startBatchTracking(resp);
      notify.info(`Revisión de ${selectedDocIds.length} documentos iniciada`);
      setSelectedDocIds([]);
    } catch (e) {
      notify.error('Error al iniciar la revisión masiva');
    } finally {
      setBulkLoading(false);
    }
  };

  // Bulk Approve (for selected documents) — async con barra de progreso.
  const handleBulkApprove = async () => {
    if (selectedDocIds.length === 0 || bulkLoading) return;
    setBulkLoading(true);
    try {
      const resp = await approvalService.approveDocumentsBatch(selectedDocIds);
      startBatchTracking(resp);
      notify.info(`Aprobación de ${selectedDocIds.length} documentos iniciada`);
      setSelectedDocIds([]);
    } catch (e) {
      notify.error('Error al iniciar la aprobación masiva');
    } finally {
      setBulkLoading(false);
    }
  };

  // Bulk Reject - show modal
  const handleBulkReject = () => {
    if (selectedDocIds.length === 0 || bulkLoading) return;
    setBulkActionType('selected');
    setBulkRejectReason('');
    setShowBulkRejectModal(true);
  };

  // Confirm Bulk Reject — async con barra de progreso.
  const confirmBulkReject = async () => {
    if (!bulkRejectReason.trim()) {
      notify.warning('Debe ingresar una razón de rechazo');
      return;
    }

    try {
      let resp;
      if (bulkActionType === 'selected') {
        resp = activeTab === 'review'
          ? await approvalService.rejectReviewDocumentsBatch(selectedDocIds, bulkRejectReason)
          : await approvalService.rejectApprovalDocumentsBatch(selectedDocIds, bulkRejectReason);
        setSelectedDocIds([]);
      } else if (bulkActionType === 'all') {
        resp = activeTab === 'review'
          ? await approvalService.rejectReviewDocumentsByFilter(currentSearchFilter, bulkRejectReason)
          : await approvalService.rejectApprovalDocumentsByFilter(currentSearchFilter, bulkRejectReason);
      }
      startBatchTracking(resp);
      notify.info('Rechazo masivo iniciado');

      setShowBulkRejectModal(false);
      setBulkRejectReason('');
      setBulkActionType(null);
    } catch (e) {
      console.error(e);
      notify.error('Error al iniciar el rechazo masivo');
    }
  };

  // ========== APPLY TO ALL ACTIONS ==========

  const handleReviewAll = () => {
    setApplyAllAction('review');
    setShowApplyAllModal(true);
  };

  const handleApproveAll = () => {
    setApplyAllAction('approve');
    setShowApplyAllModal(true);
  };

  const handleRejectAll = () => {
    setApplyAllAction('reject');
    setBulkActionType('all');
    setBulkRejectReason('');
    setShowBulkRejectModal(true);
  };

  const confirmApplyAll = async () => {
    try {
      let resp;
      if (applyAllAction === 'review') {
        resp = await approvalService.reviewDocumentsByFilter(currentSearchFilter);
      } else if (applyAllAction === 'approve') {
        resp = await approvalService.approveDocumentsByFilter(currentSearchFilter);
      }
      startBatchTracking(resp);
      notify.info('Operación masiva iniciada');

      setShowApplyAllModal(false);
      setApplyAllAction(null);
    } catch (e) {
      console.error(e);
      notify.error('Error al iniciar la operación masiva');
    }
  };

  /**
   * Texto de los metadatos obligatorios que le faltan a un documento, o cadena vacía si está
   * completo. Una sola redacción para los dos lugares donde se dice lo mismo: la marca de
   * advertencia sobre el nombre y el `title` del botón que queda deshabilitado.
   */
  const faltantesTexto = (doc) => {
    const missing = getMissingRequiredFieldLabels(doc, metadataSpec);
    return missing.length > 0 ? `Faltan metadatos obligatorios: ${missing.join(', ')}` : '';
  };

  /**
   * Nombre del archivo, con una marca cuando al documento le faltan metadatos obligatorios.
   *
   * Reemplaza a la vieja columna "Estado", que mostraba el enum crudo. De todo lo que decía ese
   * campo, esto es lo único que le significa algo al usuario: el documento no se puede avanzar
   * hasta completarlo.
   *
   * La marca **cubre** al botón deshabilitado de la misma fila (`approve_review`): se enciende
   * siempre que el botón se traba, y además cuando el estado es `METADATA_PENDING` aunque la
   * spec no declare obligatorios. La dirección importa — nunca hay un botón trabado sin marca
   * que lo explique.
   *
   * El detalle de qué campos faltan va en el `aria-label` y no sólo en el `title`: `title` en un
   * SVG se renderiza como elemento hijo, y con `role="img"` + `aria-label` el nombre accesible
   * gana, así que un lector de pantalla nunca lo diría.
   */
  const renderArchivo = (doc) => {
    const faltan = faltantesTexto(doc);
    const marcar = doc.status === 'METADATA_PENDING' || faltan !== '';
    const detalle = faltan || 'Faltan metadatos obligatorios';
    return (
      <span style={styles.celdaArchivo}>
        {marcar && (
          <AiOutlineWarning
            role="img"
            aria-label={detalle}
            title={detalle}
            style={styles.marcaFaltantes}
          />
        )}
        {doc.filename}
      </span>
    );
  };

  /**
   * La marca sólo se pinta en Revisión. En las otras pestañas señalaría algo que ahí no se
   * puede resolver —`metadataEditable` es únicamente `review`— y en OCR sería ruido garantizado:
   * esa bandeja lista documentos cuyo metadata **todavía no se extrajo**, así que la advertencia
   * se encendería en todas las filas. Un indicador que se prende siempre no informa nada; sería
   * el mismo defecto de la columna "Estado" con otro ícono.
   */
  const getCustomColumns = () => columnasDeLaPestaña.fixed.map(
    (col) => (col.key === 'filename' && activeTab === 'review'
      ? { ...col, render: renderArchivo }
      : col),
  );

  const getCustomActions = () => {
    const viewerOperation = (activeTab === 'approval' || (!isReviewer && isApprover)) ? 'approval' : 'review';
    const viewAction = {
      key: 'view',
      icon: <FaEye />,
      label: 'Ver Documento',
      onClick: (doc) => { setViewingDocId(doc.idDoc); setShowPDFViewer(true); }
    };

    if (activeTab === 'ocr') {
      return [
        viewAction,
        { key: 'reject', icon: <AiOutlineClose />, label: 'Rechazar', onClick: handleReject }
      ];
    }

    if (activeTab === 'review') {
      return [
        viewAction,
        {
          key: 'ocr',
          icon: <AiOutlineCloudServer />,
          label: 'Re-OCR',
          onClick: handleRerunOCR,
          disabled: (doc) => {
            const tipo = availableTypes.find(t => t.id === doc.idTipoDoc);
            if (!tipo) return true;
            const ocrReq = tipo.ocrRequired;
            return !(ocrReq === true || ocrReq === 1 || (typeof ocrReq === 'string' && /^(true|1|si|sí)$/i.test(String(ocrReq).trim())));
          },
          title: (doc) => {
            const tipo = availableTypes.find(t => t.id === doc.idTipoDoc);
            const ocrReq = tipo && tipo.ocrRequired;
            const hasOcr = ocrReq === true || ocrReq === 1 || (typeof ocrReq === 'string' && /^(true|1|si|sí)$/i.test(String(ocrReq).trim()));
            return hasOcr ? 'Reejecutar OCR del documento' : 'OCR no requerido para esta serie documental';
          }
        },
        {
          key: 'approve_review',
          icon: <AiOutlineCheck />,
          label: 'Aprobar Revisión',
          onClick: handleCompleteReview,
          disabled: (doc) => getMissingRequiredFieldLabels(doc, metadataSpec).length > 0,
          title: (doc) => faltantesTexto(doc) || 'Aprobar la revisión del documento'
        },
        { key: 'reject', icon: <AiOutlineClose />, label: 'Rechazar', onClick: handleReject }
      ];
    }

    if (activeTab === 'approval') {
      return [
        viewAction,
        { key: 'approve', icon: <AiOutlineCheck />, label: 'Aprobar', onClick: handleApprove },
        { key: 'reject', icon: <AiOutlineClose />, label: 'Rechazar', onClick: handleReject }
      ];
    }

    if (activeTab === 'signature') {
      return [
        viewAction,
        { key: 'sign', icon: <FaPenNib />, label: 'Firmar', onClick: (doc) => setSignDoc(doc) },
        { key: 'reject', icon: <AiOutlineClose />, label: 'Rechazar', onClick: handleReject }
      ];
    }

    // rejected
    return [{
      key: 'reason',
      icon: <AiOutlineFileText />,
      label: 'Ver descripción del rechazo',
      onClick: (doc) => {
        setSelectedRejectedDoc(doc);
        setShowRejectedReasonModal(true);
      }
    }];
  };

  const renderCountBadge = (count, label, shortLabel) => {
    const value = count ?? 0;
    const isZero = value === 0;
    return (
      <span
        key={label}
        title={label}
        style={{
          ...styles.countBadge,
          ...(isZero ? styles.countBadgeZero : styles.countBadgeActive),
        }}
      >
        {shortLabel}: {value}
      </span>
    );
  };

  // Keyboard shortcuts (P2 #69): A aprobar/revisar batch, R rechazar batch.
  // Solo se disparan cuando hay al menos un doc seleccionado, el user puede aprobar/revisar
  // en el tab actual y no hay modales abiertos.
  const noModalOpen = !showRejectModal && !showPDFViewer && !showBulkRejectModal && !signDoc;
  useKeyboardShortcuts({
    a: () => {
      if (!noModalOpen || bulkLoading || selectedDocIds.length === 0) return;
      if (activeTab === 'review' && isReviewer) handleBulkReview();
      else if (activeTab === 'approval' && isApprover) handleBulkApprove();
    },
    r: () => {
      if (!noModalOpen || bulkLoading || selectedDocIds.length === 0) return;
      if (activeTab === 'review' || activeTab === 'approval') handleBulkReject();
    },
  });

  if (!hasAccess) return null;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.titleSection}>
          <AiOutlineCheck style={styles.titleIcon} />
          <h1 style={styles.title}>Control de calidad</h1>
        </div>
        {onBack && (
          <button style={styles.backButton} onClick={onBack}>
            <AiOutlineClose style={styles.backIcon} />
            Cerrar
          </button>
        )}
      </div>

      {activeBatchTask && (
        <BatchProgressBar
          task={activeBatchTask}
          onCancel={handleCancelBatch}
          onClose={handleCloseBatch}
        />
      )}

      <div style={styles.content}>
        {/* Panel izquierdo: listado de series documentales */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Series Documentales</h2>
          {loadingTypes ? (
            <div style={styles.loading}>Cargando series...</div>
          ) : availableTypes.length === 0 ? (
            <div style={styles.loading}>No hay series documentales.</div>
          ) : (
            <div style={styles.treeWrapper} data-testid="qc-series-tree">
              <DocTreeSidebar
                folders={arbolTree}
                selectedPath={selectedTreePath}
                onFolderClick={handleTreeNodeClick}
                title=""
                storageKey="boveda.approval.expanded"
                noFetchCounts
                pillsPosition="below"
                rowHeight={48}
                emptyMessage="No hay series ni carpetas accesibles"
                containerStyle={{ width: '100%', borderRight: 'none', backgroundColor: '#FFFFFF', padding: '8px' }}
                renderSeriePills={(serieId) => {
                  const counts = serieCounts[serieId] || {};
                  return (
                    <>
                      {renderCountBadge(counts.totalDocCount, 'Total Disponibles', 'Total')}
                      {renderCountBadge(counts.ocrPendingDocCount, 'Pendientes de OCR', 'OCR')}
                      {renderCountBadge(counts.reviewPendingDocCount, 'Pendientes de revisión', 'Rev')}
                      {renderCountBadge(counts.approvalPendingDocCount, 'Pendientes de aprobación', 'Apr')}
                      {renderCountBadge(counts.signaturePendingDocCount, 'Pendientes de firma', 'Fir')}
                      {renderCountBadge(counts.publishedDocCount, 'Publicados', 'Pub')}
                      {renderCountBadge(counts.rejectedDocCount, 'Rechazados', 'Rec')}
                    </>
                  );
                }}
              />
            </div>
          )}
        </div>

        {/* Panel derecho: funcionalidad actual */}
        <div style={styles.section}>
          {(selectedTipoDoc || activeTab === 'signature') ? (
            <>
              <h2 style={styles.sectionTitle}>
                {(() => {
                  const t = availableTypes.find((x) => x.id === selectedTipoDoc);
                  return t?.name || t?.descripcion || selectedTipoDoc;
                })()}
              </h2>
              <div style={styles.tabsContainerInPanel}>
                {(isReviewer || isApprover) && (
                  <button
                    style={{ ...styles.tab, ...(activeTab === 'ocr' ? styles.tabActive : {}) }}
                    onClick={() => changeTab('ocr')}
                  >
                    OCR
                  </button>
                )}
                {isReviewer && (
                  <button
                    style={{ ...styles.tab, ...(activeTab === 'review' ? styles.tabActive : {}) }}
                    onClick={() => changeTab('review')}
                  >
                    Revisión
                  </button>
                )}
                {isApprover && (
                  <button
                    style={{ ...styles.tab, ...(activeTab === 'approval' ? styles.tabActive : {}) }}
                    onClick={() => changeTab('approval')}
                  >
                    Aprobación
                  </button>
                )}
                {isSigner && (
                  <button
                    style={{ ...styles.tab, ...(activeTab === 'signature' ? styles.tabActive : {}) }}
                    onClick={() => changeTab('signature')}
                  >
                    Firma
                  </button>
                )}
                {(isReviewer || isApprover) && (
                  <button
                    style={{ ...styles.tab, ...(activeTab === 'rejected' ? styles.tabActive : {}) }}
                    onClick={() => changeTab('rejected')}
                  >
                    Rechazados
                  </button>
                )}
              </div>
              {/* Bulk Action Toolbar - shown when documents are selected */}
              {(activeTab === 'review' || activeTab === 'approval') && selectedDocIds.length > 0 && (
                <div style={styles.bulkActionBar}>
                  <span style={{ fontWeight: 'bold', marginRight: 20 }}>
                    {selectedDocIds.length} documento{selectedDocIds.length > 1 ? 's' : ''} seleccionado{selectedDocIds.length > 1 ? 's' : ''}
                  </span>
                  {activeTab === 'review' ? (
                    <>
                      <button onClick={handleBulkReview} disabled={bulkLoading} style={{ ...styles.bulkActionButton, ...(bulkLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }} title="Atajo: A">
                        <AiOutlineCheck /> {bulkLoading ? 'Procesando...' : 'Revisar Seleccionados'}
                      </button>
                      <button onClick={handleBulkReject} disabled={bulkLoading} style={{ ...styles.bulkActionButton, backgroundColor: '#D32F2F', ...(bulkLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }} title="Atajo: R">
                        <AiOutlineClose /> Rechazar Seleccionados
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={handleBulkApprove} disabled={bulkLoading} style={{ ...styles.bulkActionButton, ...(bulkLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }} title="Atajo: A">
                        <AiOutlineCheck /> {bulkLoading ? 'Procesando...' : 'Aprobar Seleccionados'}
                      </button>
                      <button onClick={handleBulkReject} disabled={bulkLoading} style={{ ...styles.bulkActionButton, backgroundColor: '#D32F2F', ...(bulkLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }} title="Atajo: R">
                        <AiOutlineClose /> Rechazar Seleccionados
                      </button>
                    </>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6B7280', alignSelf: 'center' }}>
                    Atajos: <kbd style={{ padding: '1px 5px', background: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: 3 }}>A</kbd> acción principal · <kbd style={{ padding: '1px 5px', background: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: 3 }}>R</kbd> rechazar
                  </span>
                  <button
                    onClick={() => setSelectedDocIds([])}
                    style={{ ...styles.bulkActionButton, backgroundColor: '#757575' }}
                  >
                    Limpiar Selección
                  </button>
                </div>
              )}

              {/* Firma masiva: barra de acción sobre selección */}
              {activeTab === 'signature' && isSigner && selectedDocIds.length > 0 && (
                <div style={styles.bulkActionBar}>
                  <span style={{ fontWeight: 'bold', marginRight: 20 }}>
                    {selectedDocIds.length} documento{selectedDocIds.length > 1 ? 's' : ''} seleccionado{selectedDocIds.length > 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={() => setBatchSign({ docIds: selectedDocIds, count: selectedDocIds.length })}
                    style={styles.bulkActionButton}
                  >
                    Firmar Seleccionados
                  </button>
                </div>
              )}
              {activeTab === 'signature' && isSigner && currentSearchFilter && (
                <div style={styles.applyAllBar}>
                  <span style={{ fontWeight: 'bold', marginRight: 20 }}>
                    Aplicar a todos los documentos del filtro actual:
                  </span>
                  <button
                    onClick={() => setBatchSign({ filter: currentSearchFilter, count: null })}
                    style={styles.applyAllButton}
                  >
                    Firmar Todos
                  </button>
                </div>
              )}

              {/* Apply to All Toolbar */}
              {(activeTab === 'review' || activeTab === 'approval') && currentSearchFilter && (
                <div style={styles.applyAllBar}>
                  <span style={{ fontWeight: 'bold', marginRight: 20 }}>
                    Aplicar a todos los documentos del filtro actual:
                  </span>
                  {activeTab === 'review' ? (
                    <>
                      <button onClick={handleReviewAll} style={styles.applyAllButton}>
                        <AiOutlineCheck /> Revisar Todos
                      </button>
                      <button onClick={handleRejectAll} style={{ ...styles.applyAllButton, backgroundColor: '#D32F2F' }}>
                        <AiOutlineClose /> Rechazar Todos
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={handleApproveAll} style={styles.applyAllButton}>
                        <AiOutlineCheck /> Aprobar Todos
                      </button>
                      <button onClick={handleRejectAll} style={{ ...styles.applyAllButton, backgroundColor: '#D32F2F' }}>
                        <AiOutlineClose /> Rechazar Todos
                      </button>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'signature' && isSigner && (
                <BatchResumeBanner
                  onResume={(sel) => setBatchSign(sel)}
                  onCancel={async ({ batchId }) => { await signatureService.cancelBatch(batchId); }}
                />
              )}

              <Buscador
                systemFields={camposDeSistema}
                key={selectedTipoDoc} // Remount only on serie change; preservar filtros al cambiar tab (P2 #66)
                tipoSeleccionado={selectedTipoDoc}
                paginacion={pagination}
                setPaginacion={setPagination}
                onSearchOverride={handleSearchOverride}
                customColumns={getCustomColumns()}
                optionalColumns={columnasDeLaPestaña.optional}
                // Una clave por pestaña: lo que sirve mirando rechazados no es lo mismo que lo
                // que sirve mirando pendientes de firma.
                columnPrefsKey={`qc.${activeTab}`}
                customActions={getCustomActions()}
                hideDefaultActions={true}
                allowEmptySearch={true} // Permitir buscar sin filtros explícitos (trae todo lo del tipo)
                metadataEditable={activeTab === 'review'} // solo en revisión
                onMetadataEdit={handleMetadataEdit}
                refreshTrigger={refreshTrigger}
                selectable={activeTab === 'review' || activeTab === 'approval' || activeTab === 'signature'}
                selectedIds={selectedDocIds}
                onSelectionChange={setSelectedDocIds}
              />
            </>
          ) : (
            <div style={styles.emptyState}>
              <AiOutlineFileText size={48} />
              <p>Selecciona una serie documental para ver los documentos pendientes.</p>
            </div>
          )}
        </div>
      </div>

      {showPDFViewer && (
        <PDFViewer
          documentId={viewingDocId}
          onClose={() => setShowPDFViewer(false)}
          /* 'signature' y no 'view' (#119): stream/view exige ROLE_VIEWER, así que un firmante
             mono-rol no podía mirar el PDF antes de firmarlo. stream/signature es su equivalente
             —igual que review y approval para los otros roles— y sólo sirve documentos que estén
             pendientes de firma. */
          operation={activeTab === 'signature'
            ? 'signature'
            : (activeTab === 'approval' || (!isReviewer && isApprover)) ? 'approval' : 'review'}
        />
      )}

      {/* Modal de firma con el cliente local (#59 F1C) */}
      <SignDocumentModal
        open={!!signDoc}
        doc={signDoc}
        onClose={() => setSignDoc(null)}
        onSigned={handleSigned}
      />
      <BatchSignatureModal
        open={!!batchSign}
        selection={batchSign}
        onClose={() => setBatchSign(null)}
        onDone={() => { setBatchSign(null); setSelectedDocIds([]); setRefreshTrigger((p) => p + 1); }}
      />

      {showRejectedReasonModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3>Descripción del rechazo</h3>
            <div style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>
              {selectedRejectedDoc?.rejectedReason || 'Sin descripción de rechazo.'}
            </div>
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowRejectedReasonModal(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3>Rechazar Documento</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Razón del rechazo..."
              style={{ width: '100%', minHeight: 100, marginTop: 10, padding: 8 }}
            />
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowRejectModal(false)}>Cancelar</button>
              <button onClick={confirmReject} style={{ backgroundColor: 'red', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 4 }}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reject Modal */}
      {showBulkRejectModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3>Rechazar Documentos</h3>
            <p>
              {bulkActionType === 'selected'
                ? `Vas a rechazar ${selectedDocIds.length} documento${selectedDocIds.length > 1 ? 's' : ''}.`
                : 'Vas a rechazar TODOS los documentos que coincidan con el filtro actual.'}
            </p>
            <textarea
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              placeholder="Razón del rechazo..."
              style={{ width: '100%', minHeight: 100, marginTop: 10, padding: 8 }}
            />
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setShowBulkRejectModal(false); setBulkActionType(null); }}>Cancelar</button>
              <button
                onClick={confirmBulkReject}
                style={{ backgroundColor: 'red', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 4 }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply to All Confirmation Modal */}
      {showApplyAllModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3>Confirmar Operación</h3>
            <p>
              Esta operación se aplicará a TODOS los documentos que coincidan con tu filtro actual
              (tipo: {availableTypes.find(t => t.id === selectedTipoDoc)?.name}).
            </p>
            <p style={{ fontWeight: 'bold', color: '#D32F2F' }}>
              Mostrando {pagination.total || 0} documento{pagination.total !== 1 ? 's' : ''}.
            </p>
            <p>¿Estás seguro de que deseas continuar?</p>
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => { setShowApplyAllModal(false); setApplyAllAction(null); }}>Cancelar</button>
              <button
                onClick={confirmApplyAll}
                style={{ backgroundColor: '#00468B', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 4 }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#F8F9FA',
    minHeight: '100vh',
  },
  celdaArchivo: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  marcaFaltantes: {
    // `warning` (#F59E0B) da 2,15:1 sobre el blanco de la fila y WCAG 1.4.11 pide 3:1 para un
    // objeto gráfico que porta información. `warningDark` da 3,19:1.
    color: colors.warningDark,
    flexShrink: 0,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleIcon: {
    fontSize: '28px',
    color: '#00468B',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#00468B',
  },
  backButton: {
    backgroundColor: '#6B7280',
    color: '#FFFFFF',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  backIcon: {
    fontSize: '16px',
  },
  tabsContainer: { display: 'flex', padding: '0 20px', backgroundColor: 'white', borderBottom: '1px solid #eee' },
  tabsContainerInPanel: {
    display: 'flex',
    margin: '-20px -20px 20px -20px',
    padding: '0 20px',
    backgroundColor: 'white',
    borderBottom: '1px solid #eee'
  },
  tab: { padding: '15px 20px', background: 'none', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontSize: 16 },
  tabActive: { borderBottom: '2px solid #00468B', color: '#00468B', fontWeight: 'bold' },
  content: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: '30px',
    padding: 20,
    flex: 1,
    overflow: 'auto'
  },
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  sectionTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151',
  },
  loading: {
    textAlign: 'center',
    padding: '20px',
    color: '#888',
    fontStyle: 'italic'
  },
  seriesTable: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  treeWrapper: {
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    overflow: 'hidden',
    height: '70vh',
    minHeight: '400px',
    display: 'flex',
  },
  tableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    borderBottom: '1px solid #F3F4F6'
  },
  tableRowSelected: {
    backgroundColor: '#F0F9FF'
  },
  tableCell: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#374151',
    borderBottom: '1px solid #F3F4F6',
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 20,
    fontSize: 10,
    padding: '2px 6px',
    borderRadius: 10
  },
  countBadgeZero: {
    backgroundColor: '#E0E0E0',
    color: '#757575',
    border: '1px solid #BDBDBD'
  },
  countBadgeActive: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    border: '1px solid #C8E6C9'
  },
  emptyState: {
    textAlign: 'center',
    padding: 40,
    color: '#888'
  },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', padding: 30, borderRadius: 8, width: 500, maxWidth: '90%' },
  bulkActionBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#E3F2FD',
    borderRadius: 4,
    marginBottom: 15,
    border: '1px solid #90CAF9'
  },
  bulkActionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    marginRight: 10,
    backgroundColor: '#00468B',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 'bold'
  },
  applyAllBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#FFF3E0',
    borderRadius: 4,
    marginBottom: 15,
    border: '1px solid #FFB74D'
  },
  applyAllButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    marginRight: 10,
    backgroundColor: '#00468B',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 'bold'
  }
};

export default ApprovalPage;
